import { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { getChoresClient } from "sj-ui-toolkit/src/client";
import { useToast } from "sj-ui-toolkit/src/hooks";

interface ArchiveAssessmentDialogProps {
  visible: boolean;
  targetId: string;
  targetName: string;
  /** true = archive the assessment; false = unarchive it */
  isArchiving: boolean;
  onComplete: (completed: boolean) => void;
}

export default function ArchiveAssessmentDialog({
  visible,
  targetId,
  targetName,
  isArchiving,
  onComplete,
}: ArchiveAssessmentDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const header = isArchiving ? "Archive Assessment" : "Unarchive Assessment";

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const client = getChoresClient();
      if (isArchiving) {
        await client.archive(targetId);
      } else {
        await client.unarchive(targetId);
      }
      onComplete(true);
    } catch (e) {
      console.error(e);
      showToast({
        severity: "error",
        detail: `There was an error ${isArchiving ? "archiving" : "unarchiving"} this assessment. Please try again.`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const footer = (
    <div className="flex gap-2">
      <Button
        label="Cancel"
        severity="secondary"
        disabled={isLoading}
        onClick={() => onComplete(false)}
      />
      <Button label="Confirm" loading={isLoading} onClick={handleConfirm} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={() => onComplete(false)}
      header={header}
      footer={footer}
      maskStyle={{ backdropFilter: "blur(5px)" }}
    >
      {isArchiving ? (
        <>
          <p className="p-1">
            Are you sure you want to archive the assessment <strong>{targetName}</strong>?
          </p>
          <p className="p-1">
            This will remove it from any assigned user&apos;s home page, but will not delete any
            user data.
          </p>
        </>
      ) : (
        <>
          <p className="p-1">
            Are you sure you want to unarchive the assessment <strong>{targetName}</strong>?
          </p>
          <p className="p-1">
            It will restore it to any assigned user&apos;s home page. Any in-progress assessment
            will also be restored.
          </p>
        </>
      )}
    </Dialog>
  );
}
